import { useMemo } from "react";

import type { PositionRolePayGapData } from "../PaygapPositionRoleOverview";

// Chart constants
const CHART_HEIGHT = 550;
const BAR_WIDTH = 12;
const GROUP_SPACING = 24;
const LEFT_PADDING = 60;
const RIGHT_PADDING = 20;
const BOTTOM_PADDING = 200;
const TOP_PADDING = 20;
const TICK_COUNT = 5;

interface ChartDimensionsReturn {
  chartHeight: number;
  chartWidth: number;
  barWidth: number;
  leftPadding: number;
  rightPadding: number;
  bottomPadding: number;
  contentHeight: number;
  maxY: number;
  step: number;
  tickCount: number;
}

/**
 * Hook to calculate chart dimensions and Y-axis scale
 */
export const useChartDimensions = (
  data: PositionRolePayGapData[],
): ChartDimensionsReturn => {
  const contentHeight = CHART_HEIGHT - TOP_PADDING - BOTTOM_PADDING;
  const chartWidth =
    LEFT_PADDING + RIGHT_PADDING + data.length * (BAR_WIDTH + GROUP_SPACING);

  const { maxY, step } = useMemo(() => {
    // Determine Y scale from data
    const maxValue = Math.max(
      0,
      ...data.flatMap((d) => [d.menAverage || 0, d.womenAverage || 0]),
    );

    const rawStep = maxValue / TICK_COUNT || 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const stepCandidates = [1, 2, 5, 10];
    const chosen = stepCandidates.find((s) => s * magnitude >= rawStep) || 1;
    const calculatedStep = chosen * magnitude;
    const calculatedMaxY =
      Math.ceil((maxValue || 1) / calculatedStep) * calculatedStep;

    return {
      maxY: calculatedMaxY,
      step: calculatedStep,
    };
  }, [data]);

  return {
    chartHeight: CHART_HEIGHT,
    chartWidth,
    barWidth: BAR_WIDTH,
    leftPadding: LEFT_PADDING,
    rightPadding: RIGHT_PADDING,
    bottomPadding: BOTTOM_PADDING,
    contentHeight,
    maxY,
    step,
    tickCount: TICK_COUNT,
  };
};
